import { Colors } from '@amelio/ui-library';

import type { EmojiOption } from '@/components/AnswerEmojiList/types';
import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { AnswerEmojiList } from '@/components/AnswerEmojiList/AnswerEmojiList';
import { AnswerOptionsList, type AnswerOptionConfig } from '@/components/AnswerOptionsList';
import { CheckboxQuestionAnswer } from '@/components/CheckboxQuestionAnswer';
import { PlainTextQuestionAnswer } from '@/components/PlainTextQuestionAnswer/PlainTextQuestionAnswer';
import { RankingQuestionAnswer } from '@/components/RankingQuestionAnswer/RankingQuestionAnswer';
import { RatingScaleQuestionAnswer } from '@/components/RatingScaleQuestionAnswer/RatingScaleQuestionAnswer';
import { SectionTitleQuestionAnswer } from '@/components/SectionTitleQuestionAnswer/SectionTitleQuestionAnswer';
import { YesNoQuestionAnswer } from '@/components/YesNoQuestionAnswer/YesNoQuestionAnswer';
import { QuestionType, type QuestionTypeValue } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getEmojiSrc } from '@/utils/emojis';
import { getLocalizedText } from '@/utils/localization';

interface AnswerProps {
    question: ReviewQuestion;
    type: QuestionTypeValue;
    selectedValues: string[];
    onChange: (values: string[]) => void;
    onEnterPress?: () => void;
    /** Fired by the widgets that report a value before the answer is settled, once it settles. */
    onCommit?: () => void;
    onInteractionStart?: () => void;
    reviewee?: User;
}
export const Answer = ({
    question,
    type,
    selectedValues,
    onChange,
    onEnterPress,
    onCommit,
    onInteractionStart,
    reviewee,
}: AnswerProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const renderedQuestionLabel = replaceDynamicTags({
        text: getLocalizedText(question.label),
        employee: reviewee,
        competencyName: question.competencyName,
    });

    const renderOptionLabel = (label: Record<string, string> | undefined): string =>
        replaceDynamicTags({
            text: getLocalizedText(label),
            employee: reviewee,
            competencyName: question.competencyName,
        });

    const allOptions: AnswerOptionConfig[] =
        question.reviewQuestionOptions?.map((option) => ({
            label: renderOptionLabel(option.label),
            value: option.id,
        })) ?? [];

    switch (type) {
        case QuestionType.EMOJI_CHOICE: {
            const totalEmojiOptions = question.reviewQuestionOptions?.length ?? 0;
            const emojiOptions: EmojiOption[] =
                question.reviewQuestionOptions?.map((option, index) => {
                    /* SURVEY_EMOJI_SOURCES is ordered worst → best, scoreValue starts at 1 = worst */
                    const emojiIndex = Math.max(
                        0,
                        Math.min(totalEmojiOptions - 1, option.scoreValue - 1),
                    );

                    return {
                        id: `emoji-${index}`,
                        src: getEmojiSrc(emojiIndex),
                        label: renderOptionLabel(option.label),
                    };
                }) ?? [];

            return (
                <AnswerEmojiList
                    emojis={emojiOptions}
                    selectedValues={selectedValues}
                    onChange={onChange}
                    selectedBorderColor={Colors.PRIMARY}
                    unselectedTextColor={Colors.WHITE}
                />
            );
        }
        case QuestionType.PLAIN_TEXT:
            return (
                <PlainTextQuestionAnswer
                    selectedValues={selectedValues}
                    onChange={onChange}
                    ariaLabel={renderedQuestionLabel}
                    onEnterPress={onEnterPress}
                    autoFocus
                />
            );
        case QuestionType.RANKING: {
            const rankingOptions =
                question.reviewQuestionOptions?.map((option) => ({
                    id: option.id,
                    label: renderOptionLabel(option.label),
                })) || [];

            return (
                <RankingQuestionAnswer
                    options={rankingOptions}
                    rankedOptionIds={selectedValues}
                    onRankingChange={onChange}
                />
            );
        }
        case QuestionType.RATING_SCALE: {
            const sortedOptions = [...(question.reviewQuestionOptions ?? [])].sort(
                (a, b) => a.sortOrder - b.sortOrder,
            );

            return (
                <RatingScaleQuestionAnswer
                    selectedValues={selectedValues}
                    onChange={onChange}
                    onCommit={onCommit}
                    onInteractionStart={onInteractionStart}
                    minValue={sortedOptions[0]?.scoreValue ?? 1}
                    maxValue={sortedOptions[1]?.scoreValue ?? 7}
                    startLabel={renderOptionLabel(sortedOptions[0]?.label) || t('common.low')}
                    endLabel={renderOptionLabel(sortedOptions[1]?.label) || t('common.high')}
                />
            );
        }
        case QuestionType.SECTION_TITLE:
            return (
                <SectionTitleQuestionAnswer
                    title={renderedQuestionLabel}
                    selectedValues={selectedValues}
                    onChange={onChange}
                />
            );
        case QuestionType.YES_NO:
            return (
                <YesNoQuestionAnswer
                    selectedValues={selectedValues}
                    onChange={onChange}
                />
            );
        case QuestionType.CHECKBOX:
            return (
                <CheckboxQuestionAnswer
                    selectedValues={selectedValues}
                    options={allOptions}
                    onAnswerChange={onChange}
                    maxSelection={question.maximumChoices ?? undefined}
                />
            );
        case QuestionType.MULTI_CHOICE:
            return (
                <AnswerOptionsList
                    options={allOptions}
                    selectedValues={selectedValues}
                    onChange={onChange}
                    multiple={false}
                />
            );
        default:
            return (
                <AnswerOptionsList
                    options={allOptions}
                    selectedValues={selectedValues}
                    onChange={onChange}
                    multiple={false}
                    maxSelection={question.maximumChoices ?? undefined}
                />
            );
    }
};
